import React, { useRef, useState } from "react";

// @ts-expect-error The existing JavaScript API helpers have no type declarations.
import { makeDeleteApiCall, makeGetApiCall, makePostApiCall, makePutApiCall } from "../../shared/makeApiCall";
// @ts-expect-error The existing JavaScript notification has no type declarations.
import { BannerNotificationType } from "../../components/SimpleNotification";
// @ts-expect-error The existing JavaScript error modal has no type declarations.
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
// @ts-expect-error The existing JavaScript utilities have no type declarations.
import { generateUniqueNumber } from "../../shared/utility";
// @ts-expect-error The existing JavaScript confirmation form has no type declarations.
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ProgressModal from "../../components/modals/ProgressModal";
import type { SummaryDiscountPayload } from "../../components/forms/orders/summaryPricingTypes";
import type {
  ApiResult, ModalState, MutationOptions, NotificationState, PriceGroupPayload,
  PriceSplitPayload, PricingMutationData, ShipmentDetails, ShipmentFormValues,
  ShipmentOption, SourceItemUpdatePayload, TaxGroupPayload, TaxRateOption,
} from "./orderDetailsTypes";

// Type the legacy API boundary without changing its requests or error handling.
const getApi = makeGetApiCall as <Data>(endpoint: string) => Promise<ApiResult<Data>>;
const putApi = makePutApiCall as <Data = PricingMutationData>(endpoint: string, payload: object) => Promise<ApiResult<Data>>;
const postApi = makePostApiCall as <Data = PricingMutationData>(endpoint: string, payload?: object) => Promise<ApiResult<Data>>;
const deleteApi = makeDeleteApiCall as (endpoint: string) => Promise<ApiResult<PricingMutationData>>;

export function OrderDetailsViewModel(logout: () => void, orderId: number | string, addSnackbar: (notification: SnackbarData) => void) {
  const [carriers, setCarriers] = useState<ShipmentOption[]>([]);
  const [customers, setCustomers] = useState<ShipmentOption[]>([]);
  const [emptyModal, setEmptyModelOpen] = useState<ModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [showSimpleNotification, setShowSimpleNotification] = useState<NotificationState>({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState<ModalState>({
    childComponent: null,
    open: false,
  });
  const [order, setOrder] = useState<ShipmentDetails>({ invoice_number: "" });
  const [taxRateOptions, setTaxRateOptions] = useState<TaxRateOption[]>([]);
  const latestOrderDetailsRequest = useRef(0);

  const showProgressModal = (message: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ProgressModal message={message} />
      ),
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const getOrderDetails = async function () {
    const requestId = ++latestOrderDetailsRequest.current;
    const callResult = await getApi<ShipmentDetails>(`orders/${orderId}`);

    // A slower earlier refresh must not overwrite a newer mutation result.
    if (requestId !== latestOrderDetailsRequest.current) {
      return callResult;
    }

    if (callResult.success) {
      setOrder(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);

    return callResult;
  };

  const getCarriersForDropdown = async function () {
    let callResult = await getApi<ShipmentOption[]>(`carriersForDropdown`);
    if (callResult.success) {
      setCarriers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCustomersForDropdown = async function () {
    let callResult = await getApi<ShipmentOption[]>(`customersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getTaxRatesForDropdown = async function () {
    let callResult = await getApi<TaxRateOption[]>("taxRatesForDropdown");
    if (callResult.success) {
      setTaxRateOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title?: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const onMakeOrderApiCall = async function (payload: ShipmentFormValues) {
    var customer_id: number | null = null;
    if (payload.customer) {
      customer_id = payload.customer.value;
    }

    var carrier_id: number | null = null;
    if (payload.carrier) {
      carrier_id = payload.carrier.value;
    }

    var status: number | null = null;
    if (payload.status) {
      status = payload.status.value;
    }

    const newPayload = {
      ...payload,
      customer_id: customer_id,
      carrier_id: carrier_id,
      status: status,
      memo: payload.memo ?? "",
      invoice_number: payload.invoice_number ?? "",
      truck_question_1: payload.truck_question_1.value,
      truck_question_2: payload.truck_question_2.value,
      truck_question_3: payload.truck_question_3.value,
      truck_question_4: payload.truck_question_4.value,
    };
    let callResult = await putApi(`orders/${orderId}`, newPayload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Order updated",
          SnackbarType.Success
        )
      );
      getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const updateOrderApiCall = async function (payload: ShipmentFormValues) {
    showProgressModal("Updating order...");
    var allOrdersHavePrice = true;

    if (payload.status?.value === 6) {
      for (const carton of payload.mixed_cartons || []) {
        for (const item of carton.source_items || []) {
          if (parseFloat(String(item.subtotal_price || 0)) <= 0) {
            allOrdersHavePrice = false;
          }
        }
      }
    }

    if (allOrdersHavePrice) {
      hideProgressModal();
      await onMakeOrderApiCall(payload);
    } else {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Some products have no price. Review line items or choose Continue to verify anyway.",
          SnackbarType.Error
        )
      );
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            title="Missing unit prices"
            buttonMessage="Continue"
            confirmAction={() => onMakeOrderApiCall(payload)}
            message="One or more order items have no unit cost. Do you want to continue verifying this order?"
            hideReason={true}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const handleOrderItemMutationResult = <Data,>(callResult: ApiResult<Data>, successMessage: string, {
    waitForRefresh = false,
    inlineErrors = false,
    refreshOnConflict = false,
  }: MutationOptions = {}) => {
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          successMessage,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) logout();
      if (!inlineErrors) showErrorModal(callResult.data.message);
    }

    // Inline editors retain their draft while a conflict refreshes its snapshot.
    if (callResult.success || (refreshOnConflict && callResult.errorStatus === 409)) {
      const refresh = getOrderDetails();
      if (waitForRefresh) return refresh.then(() => callResult);
    }
    return callResult;
  };

  const onUpdateOrderItem = async function (sourceItem: SourceItemUpdatePayload) {
    let callResult = await putApi(
      `/orders/source-item/update`,
      sourceItem,
    );
    return handleOrderItemMutationResult(callResult, "Item updated");
  };

  const onSavePluPriceGroup = async function (payload: PriceGroupPayload) {
    const callResult = await putApi(
      `/orders/${orderId}/plu-price-groups`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Price group updated");
  };

  const onSavePluTaxGroup = async function (payload: TaxGroupPayload) {
    const callResult = await putApi(
      `/orders/${orderId}/plu-tax-groups`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Taxes updated", { waitForRefresh: true });
  };

  const onUpdateSummaryDiscount = async function (payload: SummaryDiscountPayload) {
    const callResult = await putApi(
      `/orders/${orderId}/source-item-discounts`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Row discount updated", {
      waitForRefresh: true,
      inlineErrors: true,
      refreshOnConflict: true,
    });
  };

  const onSetProductPromotionBypass = async function (productId: number, bypassed: boolean) {
    const callResult = await putApi(
      `/orders/${orderId}/products/${productId}/promotion-bypass`,
      { bypassed },
    );
    return handleOrderItemMutationResult(
      callResult,
      bypassed ? "Manual pricing enabled" : "Promotions allowed",
      { waitForRefresh: true },
    );
  };

  const onCreatePriceSplit = async function (sourceItemId: number, payload: PriceSplitPayload) {
    const callResult = await postApi(
      `/orders/source-items/${sourceItemId}/price-splits`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Price split added");
  };

  const onUpdatePriceSplit = async function (sourceItemId: number, splitId: number, payload: PriceSplitPayload) {
    const callResult = await putApi(
      `/orders/source-items/${sourceItemId}/price-splits/${splitId}`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Price split updated");
  };

  const onDeletePriceSplit = async function (sourceItemId: number, splitId: number) {
    const callResult = await deleteApi(
      `/orders/source-items/${sourceItemId}/price-splits/${splitId}`,
    );
    return handleOrderItemMutationResult(callResult, "Price split deleted");
  };

  const onUpdateSourceItemTax = async function (sourceItemId: number, taxes: TaxRateOption[] | null) {
    const callResult = await putApi(`/orders/source-item/taxes`, {
      id: sourceItemId,
      taxes: (taxes ?? []).map((tax) => ({ value: tax.value })),
    });
    return handleOrderItemMutationResult(callResult, "Taxes updated", { waitForRefresh: true });
  };

  const onBulkUpdateSourceItemTaxIncluded = async function (sourceItemIds: number[], taxIncluded: boolean) {
    const callResult = await putApi(
      `/orders/${orderId}/source-item-tax-included`,
      { source_item_ids: sourceItemIds, tax_included: taxIncluded },
    );
    return handleOrderItemMutationResult(callResult, "Tax Included updated", {
      waitForRefresh: true,
      inlineErrors: true,
    });
  };

  const printOrderInvoice = async function () {
    let callResult = await postApi<{ location: string }>(`generateOrderInvoice/${orderId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printShippingSummary = async function () {
    let callResult = await postApi<{ location: string }>(
      `generateShippingSummaryReport/${orderId}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printShippingDetails = async function () {
    let callResult = await postApi<{ location: string }>(
      `generateShippingDetailsReport/${orderId}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  return {
    carriers,
    getCarriersForDropdown,
    customers,
    getCustomersForDropdown,
    loading,
    showSimpleNotification,
    setEmptyModelOpen,
    setShowSimpleNotification,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateOrderApiCall,
    getOrderDetails,
    order,
    onUpdateOrderItem,
    onSavePluPriceGroup,
    onSavePluTaxGroup,
    onUpdateSummaryDiscount,
    onSetProductPromotionBypass,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    onUpdateSourceItemTax,
    onBulkUpdateSourceItemTaxIncluded,
    taxRateOptions,
    getTaxRatesForDropdown,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
  };
}
